import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Sparkles, Loader2, ChevronLeft, ChevronRight, Wand2, Download, Share2,
  MessageCircle, Camera, Upload, CheckCircle, Info, Home, FileText,
  Layers, Palette, TreePine, Droplet, ShieldCheck, AlertTriangle, X,
} from 'lucide-react';
import {
  DOOR_ROOMS, DOOR_SIZES, DOOR_HANDLES, DOOR_FRAMES, CNC_CATEGORIES,
  FINISHES, TEXTURES, LIGHTINGS, cncSvgFor, generateBrandedDesignPDF,
  DOOR_MATERIALS, VENEER_SPECIES, MICA_PALETTE, PU_PAINT_PALETTE, PU_SHEENS,
  MATERIAL_CARE, ROOM_MATERIAL_ADVICE, veneerGrainSvg,
} from '../lib/designStudio';
import ARDoorPreview from '../components/ARDoorPreview';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import KenBurnsExport from '../components/KenBurnsExport';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const materialIcon = (id) => {
  if (id === 'acrylic-laminate') return <Sparkles className="h-6 w-6" />;
  if (id === 'mica') return <Palette className="h-6 w-6" />;
  if (id === 'veneer') return <TreePine className="h-6 w-6" />;
  return <Droplet className="h-6 w-6" />;
};

const StepPill = ({ num, label, active, done }) => (
  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
    active ? 'bg-[#004534] text-white'
      : done ? 'bg-[#004534]/10 text-[#004534]'
      : 'bg-gray-100 text-gray-400'
  }`}>
    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
      done ? 'bg-[#D4AF37] text-[#004534]' : active ? 'bg-white text-[#004534]' : ''
    }`}>
      {done ? <CheckCircle className="h-3.5 w-3.5" /> : num}
    </div>
    <span className="text-xs font-medium hidden md:inline">{label}</span>
  </div>
);

const NailFreeBadge = () => (
  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#D4AF37]/20 text-[#7A6621] text-[10px] font-bold rounded-full uppercase tracking-wider">
    <ShieldCheck className="h-3 w-3" />
    Nail-Free · PUR-Bonded
  </div>
);

const RoomSuitabilityBadges = ({ roomId, materialId }) => {
  const badges = ['✓ 6H Hardness Substrate', '✓ Termite-Proof', '✓ Nail-Free CNC'];
  if (roomId === 'bathroom' || roomId === 'utility') badges.push('✓ Zero Swelling');
  if (materialId === 'acrylic-laminate' && (roomId === 'bathroom' || roomId === 'utility')) badges.push('✓ 100% Waterproof');
  if (roomId === 'kitchen') badges.push('✓ Heat-Resistant 150°C', '✓ Stain-Proof');
  if (roomId === 'main-entrance') badges.push('✓ UV-Stable', '✓ Anti-Yellowing');
  if (roomId === 'pooja') badges.push('✓ Non-Yellowing', '✓ Long-Life');
  if (materialId === 'veneer') badges.push('✓ Real Natural Wood', '✓ Ages Into Patina');
  if (materialId === 'pu-paint') badges.push('✓ Automotive-Grade Finish');
  if (materialId === 'mica') badges.push('✓ Scratch & Stain Resistant');
  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((b) => (
        <span key={b} className="px-3 py-1 bg-[#004534]/8 text-[#004534] text-xs font-semibold rounded-full">
          {b}
        </span>
      ))}
    </div>
  );
};

const AIDoorDesigner = () => {
  const [step, setStep] = useState(1);
  const [roomLocation, setRoomLocation] = useState(null);
  const [materialFamily, setMaterialFamily] = useState(null);
  const [isCnc, setIsCnc] = useState(true);
  const [cncPatterns, setCncPatterns] = useState([]);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [customCncFile, setCustomCncFile] = useState(null);
  const [customCncPreview, setCustomCncPreview] = useState(null);

  // Material-specific finish state
  const [acrylicFinish, setAcrylicFinish] = useState('wood-walnut');
  const [acrylicTexture, setAcrylicTexture] = useState('high-gloss');
  const [micaShade, setMicaShade] = useState('teakwood-classic');
  const [micaCode, setMicaCode] = useState('');
  const [veneerSpecies, setVeneerSpecies] = useState('burma-teak');
  const [puColor, setPuColor] = useState('champagne-gold');
  const [puHex, setPuHex] = useState('');
  const [puSheen, setPuSheen] = useState('matte');
  const [useCustomHex, setUseCustomHex] = useState(false);

  const [doorSize, setDoorSize] = useState('6.5x2.5');
  const [customW, setCustomW] = useState(3);
  const [customH, setCustomH] = useState(7);
  const [doubleDoor, setDoubleDoor] = useState(false);
  const [handleStyle, setHandleStyle] = useState('gold');
  const [frameMaterial, setFrameMaterial] = useState('wpc');
  const [lighting, setLighting] = useState('day');

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const [arActive, setArActive] = useState(false);
  const [beforeAfterMode, setBeforeAfterMode] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const customCncInputRef = useRef(null);

  const loadingMessages = [
    'Analyzing your CriLam material family...',
    'Milling the design in premium detail...',
    'Applying CriLam PUR-bonded finish (nail-free)...',
    'Adding hardware and framing...',
    'Rendering photorealistic lighting...',
  ];

  useEffect(() => {
    if (!isGenerating) { setLoadingMsgIndex(0); return; }
    const t = setInterval(() => setLoadingMsgIndex((i) => (i + 1) % loadingMessages.length), 7000);
    return () => clearInterval(t);
  }, [isGenerating, loadingMessages.length]);

  // Load CNC patterns when room + material are chosen and CNC path
  useEffect(() => {
    if (!roomLocation) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/cnc-patterns?room=${roomLocation}`);
        const d = await r.json();
        setCncPatterns(d.patterns || []);
        const sz = DOOR_SIZES.find((s) => s.defaultRooms.includes(roomLocation));
        if (sz) setDoorSize(sz.id);
        setDoubleDoor(roomLocation === 'main-entrance');
      } catch {
        toast.error('Could not load CNC patterns. Please retry.');
      }
    })();
  }, [roomLocation]);

  const filteredPatterns = useMemo(() => {
    if (activeCategory === 'all') return cncPatterns;
    return cncPatterns.filter((p) => p.category === activeCategory);
  }, [activeCategory, cncPatterns]);

  const availableCategories = useMemo(() => {
    const inCat = new Set(cncPatterns.map((p) => p.category));
    return CNC_CATEGORIES.filter((c) => inCat.has(c.id));
  }, [cncPatterns]);

  const currentMaterial = DOOR_MATERIALS.find((m) => m.id === materialFamily);
  const materialAdvice = roomLocation && materialFamily
    ? (ROOM_MATERIAL_ADVICE[roomLocation] || {})[materialFamily]
    : null;

  const handleCustomCncUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error('Please upload an image under 6MB.'); return; }
    setCustomCncFile(f);
    setCustomCncPreview(URL.createObjectURL(f));
    setSelectedPattern({ id: 'custom', name: 'Custom CNC Design', category: 'special', description: 'Customer-provided custom CNC pattern' });
    setStep(4);
  };

  // Build finish label for display
  const finishDisplay = useMemo(() => {
    if (!materialFamily) return '';
    if (materialFamily === 'acrylic-laminate') return FINISHES.find((f) => f.id === acrylicFinish)?.label;
    if (materialFamily === 'mica') return MICA_PALETTE.find((m) => m.id === micaShade)?.name + (micaCode ? ` · ${micaCode}` : '');
    if (materialFamily === 'veneer') return VENEER_SPECIES.find((v) => v.id === veneerSpecies)?.name;
    if (materialFamily === 'pu-paint') {
      const c = PU_PAINT_PALETTE.find((p) => p.id === puColor);
      return `${useCustomHex && puHex ? puHex : c?.name} · ${puSheen.charAt(0).toUpperCase() + puSheen.slice(1)}`;
    }
    return '';
  }, [materialFamily, acrylicFinish, micaShade, micaCode, veneerSpecies, puColor, puHex, useCustomHex, puSheen]);

  const generateDoor = async () => {
    if (!materialFamily || !roomLocation) { setError('Please choose a room and material family.'); return; }
    if (isCnc && !selectedPattern) { setError('Please pick a CNC design or switch to plain shutter.'); return; }

    setIsGenerating(true);
    setError(null);
    setResult(null);
    try {
      let customImageBase64 = null;
      if (isCnc && selectedPattern?.id === 'custom' && customCncFile) {
        customImageBase64 = await new Promise((res, rej) => {
          const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(customCncFile);
        });
      }

      const body = {
        room_location: roomLocation,
        material_family: materialFamily,
        is_cnc: isCnc,
        cnc_pattern_id: isCnc ? selectedPattern?.id : null,
        custom_cnc_image: customImageBase64,
        finish: materialFamily === 'acrylic-laminate' ? acrylicFinish
          : materialFamily === 'mica' ? micaShade : null,
        texture: materialFamily === 'acrylic-laminate' ? acrylicTexture
          : materialFamily === 'mica' ? 'matte' : null,
        veneer_species: materialFamily === 'veneer' ? veneerSpecies : null,
        mica_code: materialFamily === 'mica' ? (micaCode || null) : null,
        pu_paint_color: materialFamily === 'pu-paint' ? puColor : null,
        pu_paint_hex: materialFamily === 'pu-paint' && useCustomHex ? puHex : null,
        pu_paint_sheen: materialFamily === 'pu-paint' ? puSheen : null,
        door_size: doorSize,
        custom_width: doorSize === 'custom' ? Number(customW) : null,
        custom_height: doorSize === 'custom' ? Number(customH) : null,
        double_door: doubleDoor,
        handle_style: handleStyle,
        frame_material: frameMaterial,
        lighting,
      };

      const r = await fetch(`${API_URL}/api/door-design`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to generate door design.');
      }
      const d = await r.json();
      if (!d.success || !d.image_url) throw new Error('No image returned.');
      setResult(d);
      setStep(5);
    } catch (e) {
      setError(e.message || 'Failed to generate door design.');
    } finally {
      setIsGenerating(false);
    }
  };

  const shareToWhatsApp = async () => {
    if (!result) return;
    const roomLabel = DOOR_ROOMS.find((r) => r.id === roomLocation)?.label || '';
    const matName = result.material_name || currentMaterial?.name || '';
    const pattern = isCnc ? (result.cnc_pattern_name || selectedPattern?.name || 'CNC') : 'Plain Shutter';
    const share = `Check out my CriLam ${matName} — ${pattern} door in ${finishDisplay} for ${roomLabel}!\n\n(Nail-Free · PUR-Bonded)\n\nSee the design: ${API_URL}/api/door-design-image/${result.share_id}\n\nDesign your own: ${window.location.origin}/door-designer`;
    try {
      const res = await fetch(result.image_url);
      const blob = await res.blob();
      const file = new File([blob], 'crilam-door-design.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'My CriLam Door Design', text: share });
        return;
      }
    } catch (e) { if (e.name === 'AbortError') return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(share)}`, '_blank', 'noopener,noreferrer');
  };

  const downloadImage = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.image_url;
    a.download = `crilam-${materialFamily}-${roomLocation}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    toast.success('Design downloaded');
  };

  const downloadPDF = async () => {
    if (!result) return;
    setPdfGenerating(true);
    try {
      const care = MATERIAL_CARE[materialFamily] || [];
      const extras = [
        ['Room:', DOOR_ROOMS.find((r) => r.id === roomLocation)?.label],
        ['Material Family:', currentMaterial?.name],
        ['Design:', isCnc ? (result.cnc_pattern_name || 'CNC') : 'Plain Shutter'],
        ['Configuration:', doubleDoor ? 'Double Door' : 'Single Door'],
        ['Size:', doorSize === 'custom' ? `${customW} × ${customH} ft` : DOOR_SIZES.find((s) => s.id === doorSize)?.label],
        ['Handle:', DOOR_HANDLES.find((h) => h.id === handleStyle)?.label],
        ['Frame:', DOOR_FRAMES.find((f) => f.id === frameMaterial)?.label],
        ['Care Tip 1:', care[0] || '—'],
        ['Care Tip 2:', care[1] || '—'],
      ];
      const doc = await generateBrandedDesignPDF({
        imageDataUrl: result.image_url,
        title: `CriLam ${currentMaterial?.name} Door`,
        productLabel: `CriLam ${currentMaterial?.name} · Nail-Free · PUR-Bonded`,
        designCode: (materialFamily + '-' + (selectedPattern?.id || 'plain')).toUpperCase(),
        finishLabel: finishDisplay,
        finishCode: materialFamily.toUpperCase(),
        textureLabel: currentMaterial?.short || '',
        extraSpecs: extras,
      });
      doc.save(`crilam-${materialFamily}-door.pdf`);
      toast.success('Branded design sheet downloaded');
    } catch (e) {
      toast.error('Could not generate PDF.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const reset = () => {
    setStep(1); setRoomLocation(null); setMaterialFamily(null);
    setIsCnc(true); setSelectedPattern(null);
    setCustomCncFile(null); setCustomCncPreview(null);
    setResult(null); setError(null); setBeforeAfterMode(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="ai-door-designer-page">
      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-5xl text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              <Sparkles className="h-4 w-4" /> AI DOOR DESIGNER
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit']">
              Design Your CriLam Door
            </h1>
            <p className="text-lg text-[#D4AF37] font-medium mb-3">
              Acrylic Laminate · Mica · Natural Veneer · PU Paint — All Nail-Free, PUR-Bonded
            </p>
            <p className="text-white/70 max-w-2xl mx-auto">
              Every CriLam door is bonded with our proprietary CriLam PUR-Bond™ technology — no visible nails, sealed edges,
              10-year written guarantee. Choose your room, material family, design, finish and see it rendered in seconds.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/nail-free-advantage" className="text-xs text-white/80 hover:text-[#D4AF37] underline underline-offset-4">
                Why Nail-Free Matters →
              </Link>
              <Link to="/match-my-doors" className="text-xs text-white/80 hover:text-[#D4AF37] underline underline-offset-4">
                Match My Existing Doors (AI) →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Progress */}
      <section className="py-4 bg-white border-b sticky top-0 z-20">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <StepPill num={1} label="Room" active={step === 1} done={step > 1} />
            <ChevronRight className="h-4 w-4 text-gray-300" />
            <StepPill num={2} label="Material" active={step === 2} done={step > 2} />
            <ChevronRight className="h-4 w-4 text-gray-300" />
            <StepPill num={3} label="Design" active={step === 3} done={step > 3} />
            <ChevronRight className="h-4 w-4 text-gray-300" />
            <StepPill num={4} label="Customize" active={step === 4} done={step > 4} />
            <ChevronRight className="h-4 w-4 text-gray-300" />
            <StepPill num={5} label="Result" active={step === 5} done={false} />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 lg:py-16">
        <div className="container mx-auto px-6 lg:px-12 max-w-6xl">

          {/* Step 1: Room */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#004534] mb-2 text-center font-['Outfit']">
                Which room is this door for?
              </h2>
              <p className="text-gray-600 text-center mb-10">
                Every room has different needs. We&apos;ll suggest materials and CNC designs that work best for each.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {DOOR_ROOMS.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => { setRoomLocation(r.id); setStep(2); }}
                    className="group relative bg-white rounded-xl p-6 text-left border border-gray-200 hover:border-[#004534] hover:shadow-lg transition-all"
                    data-testid={`door-room-${r.id}`}
                  >
                    <div className="absolute top-3 right-3 px-2 py-0.5 bg-[#D4AF37] text-[#004534] text-[10px] font-bold rounded-full">
                      {r.badge}
                    </div>
                    <Home className="h-8 w-8 text-[#004534] mb-3" />
                    <h3 className="text-lg font-bold text-[#004534] mb-1">{r.label}</h3>
                    <p className="text-sm text-gray-500">{r.desc}</p>
                    <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#004534] opacity-70 group-hover:opacity-100">
                      Choose Material <ChevronRight className="h-4 w-4" />
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Material Family */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">For</div>
                  <h2 className="text-2xl font-bold text-[#004534] font-['Outfit']">
                    {DOOR_ROOMS.find((r) => r.id === roomLocation)?.label} · Choose Door Material
                  </h2>
                </div>
                <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#004534]">
                  <ChevronLeft className="h-4 w-4" /> Change Room
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                All CriLam doors use PUR-bonded nail-free construction — pick the surface material that matches your style, budget and room.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {DOOR_MATERIALS.map((m) => {
                  const advice = (ROOM_MATERIAL_ADVICE[roomLocation] || {})[m.id];
                  return (
                    <button
                      key={m.id}
                      onClick={() => { setMaterialFamily(m.id); setStep(3); }}
                      className={`group relative overflow-hidden bg-white rounded-xl border border-gray-200 hover:border-[#004534] hover:shadow-xl transition-all text-left`}
                      data-testid={`door-material-${m.id}`}
                    >
                      <div className={`h-32 bg-gradient-to-br ${m.accentGradient} relative overflow-hidden`}>
                        <div className="absolute top-3 right-3 px-2 py-0.5 bg-[#D4AF37] text-[#004534] text-[10px] font-bold rounded-full">
                          {m.tag}
                        </div>
                        <div className="absolute bottom-3 left-4 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-[#004534]">
                          {materialIcon(m.id)}
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-lg font-bold text-[#004534]">{m.name}</h3>
                          <NailFreeBadge />
                        </div>
                        <p className="text-sm text-[#7A6621] font-medium mb-2">{m.short}</p>
                        <p className="text-xs text-gray-500 mb-3">{m.long}</p>
                        <ul className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] text-gray-600">
                          {m.features.slice(0, 4).map((f) => (
                            <li key={f} className="flex items-start gap-1">
                              <CheckCircle className="h-3 w-3 text-[#004534] mt-0.5 flex-shrink-0" /> {f}
                            </li>
                          ))}
                        </ul>
                        {advice && (
                          <div className={`mt-3 flex items-start gap-1.5 text-[11px] p-2 rounded ${
                            advice.level === 'warn' ? 'bg-amber-50 text-amber-800 border border-amber-200'
                              : advice.level === 'ok' ? 'bg-green-50 text-green-800 border border-green-200'
                              : 'bg-blue-50 text-blue-800 border border-blue-200'
                          }`}>
                            {advice.level === 'warn'
                              ? <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                              : <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
                            <span>{advice.msg}</span>
                          </div>
                        )}
                        <div className="mt-3 text-sm font-semibold text-[#004534] group-hover:text-[#D4AF37] flex items-center gap-1">
                          Choose {m.name.split(' ')[0]} <ChevronRight className="h-4 w-4" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Step 3: CNC or Plain, then Design gallery */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    {DOOR_ROOMS.find((r) => r.id === roomLocation)?.label} · {currentMaterial?.name}
                  </div>
                  <h2 className="text-2xl font-bold text-[#004534] font-['Outfit']">
                    Plain shutter or CNC design?
                  </h2>
                </div>
                <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#004534]">
                  <ChevronLeft className="h-4 w-4" /> Change Material
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                <button
                  onClick={() => setIsCnc(false)}
                  className={`p-5 rounded-xl text-left transition-all ${
                    !isCnc ? 'bg-[#004534] text-white shadow-lg' : 'bg-white border border-gray-200 hover:border-[#004534]'
                  }`}
                  data-testid="door-design-plain"
                >
                  <div className={`text-lg font-bold ${!isCnc ? 'text-white' : 'text-[#004534]'}`}>Plain Shutter</div>
                  <p className={`text-sm mt-1 ${!isCnc ? 'text-[#D4AF37]' : 'text-gray-500'}`}>Clean uninterrupted surface — lets the finish shine</p>
                </button>
                <button
                  onClick={() => setIsCnc(true)}
                  className={`p-5 rounded-xl text-left transition-all relative ${
                    isCnc ? 'bg-[#004534] text-white shadow-lg' : 'bg-white border border-gray-200 hover:border-[#004534]'
                  }`}
                  data-testid="door-design-cnc"
                >
                  {currentMaterial?.cncCompat === 'medium' && (
                    <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-amber-500 text-white text-[9px] font-bold rounded-full">
                      Medium fit
                    </div>
                  )}
                  <div className={`text-lg font-bold ${isCnc ? 'text-white' : 'text-[#004534]'}`}>CNC Design</div>
                  <p className={`text-sm mt-1 ${isCnc ? 'text-[#D4AF37]' : 'text-gray-500'}`}>Precision-milled decorative patterns · 28 designs</p>
                </button>
              </div>

              {currentMaterial?.cncWarning && isCnc && (
                <div className="mb-6 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{currentMaterial.cncWarning}</span>
                </div>
              )}

              {isCnc && (
                <>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <button
                      onClick={() => setActiveCategory('all')}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                        activeCategory === 'all' ? 'bg-[#004534] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#004534]'
                      }`}
                      data-testid="cnc-category-all"
                    >
                      All Designs ({cncPatterns.length})
                    </button>
                    {availableCategories.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setActiveCategory(c.id)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                          activeCategory === c.id ? 'bg-[#004534] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#004534]'
                        }`}
                        data-testid={`cnc-category-${c.id}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>

                  <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filteredPatterns.map((p) => (
                      <motion.button
                        key={p.id}
                        onClick={() => { setSelectedPattern(p); setStep(4); }}
                        whileHover={{ y: -3 }}
                        className="group bg-white rounded-xl p-4 border border-gray-200 hover:border-[#004534] hover:shadow-md transition-all text-left"
                        data-testid={`cnc-pattern-${p.id}`}
                      >
                        <div className="aspect-[2/3] bg-gradient-to-b from-[#004534]/8 to-[#D4AF37]/10 rounded-lg overflow-hidden mb-3 flex items-center justify-center relative">
                          <svg viewBox="0 0 120 190" className="w-full h-full text-[#004534] p-2">
                            <rect x="8" y="8" width="104" height="174" rx="3" fill="white" stroke="currentColor" strokeWidth="0.6" opacity="0.6" />
                            {cncSvgFor(p.id)}
                          </svg>
                          {p.category === 'special' && (
                            <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#D4AF37] text-[#004534] text-[10px] font-bold rounded-full">
                              Featured
                            </div>
                          )}
                        </div>
                        <h4 className="font-semibold text-[#004534] text-sm">{p.name}</h4>
                        <p className="text-xs text-gray-500 capitalize mt-0.5">{p.category.replace(/-/g, ' ')}</p>
                      </motion.button>
                    ))}
                  </div>

                  <div className="mt-8 p-6 bg-gradient-to-r from-[#004534] to-[#0A0D0C] rounded-xl text-white">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold flex items-center gap-2 mb-1">
                          <Sparkles className="h-5 w-5 text-[#D4AF37]" /> Have your own CNC design in mind?
                        </h4>
                        <p className="text-sm text-white/70">
                          Upload a sketch or reference image — CriLam manufactures custom CNC doors.
                        </p>
                      </div>
                      <input ref={customCncInputRef} type="file" accept="image/*" onChange={handleCustomCncUpload} className="hidden" data-testid="custom-cnc-input" />
                      <button onClick={() => customCncInputRef.current?.click()}
                        className="px-5 py-3 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2"
                        data-testid="upload-custom-cnc-btn"
                      >
                        <Upload className="h-4 w-4" /> Upload My CNC Design
                      </button>
                    </div>
                  </div>
                </>
              )}

              {!isCnc && (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">Plain shutter chosen — the finish takes centre stage.</p>
                  <button onClick={() => setStep(4)} className="px-6 py-3 bg-[#D4AF37] text-[#004534] rounded-lg font-bold hover:bg-[#004534] hover:text-white" data-testid="continue-plain-btn">
                    Continue to Customize →
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 4: Customize (material-specific) */}
          {step === 4 && currentMaterial && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-8">
              {/* Left column: design preview + material summary */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 border border-gray-200 sticky top-20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="text-xs text-gray-500">Your Design</div>
                    <NailFreeBadge />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="aspect-[2/3] bg-gradient-to-b from-[#004534]/8 to-[#D4AF37]/10 rounded-lg overflow-hidden flex items-center justify-center relative">
                      {isCnc && customCncPreview ? (
                        <img src={customCncPreview} alt="Custom CNC" className="w-full h-full object-contain" />
                      ) : isCnc && selectedPattern ? (
                        <svg viewBox="0 0 120 190" className="w-full h-full text-[#004534] p-3">
                          <rect x="8" y="8" width="104" height="174" rx="3" fill="white" stroke="currentColor" strokeWidth="0.6" opacity="0.6" />
                          {cncSvgFor(selectedPattern.id)}
                        </svg>
                      ) : (
                        <div className="text-center p-3">
                          <Layers className="h-8 w-8 text-[#004534]/40 mx-auto mb-1" />
                          <p className="text-xs text-gray-500">Plain Shutter</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Room</p>
                      <p className="text-sm font-bold text-[#004534] mb-2">{DOOR_ROOMS.find((r) => r.id === roomLocation)?.label}</p>
                      <p className="text-xs text-gray-500 mb-0.5">Material</p>
                      <p className="text-sm font-bold text-[#004534] mb-2">{currentMaterial.name}</p>
                      <p className="text-xs text-gray-500 mb-0.5">Design</p>
                      <p className="text-sm font-bold text-[#004534]">{isCnc ? (selectedPattern?.name || 'CNC') : 'Plain Shutter'}</p>
                    </div>
                  </div>
                  <div className="pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-2">Room Suitability</p>
                    <RoomSuitabilityBadges roomId={roomLocation} materialId={materialFamily} />
                  </div>
                  {materialAdvice && (
                    <div className={`mt-3 flex items-start gap-1.5 text-[11px] p-2 rounded ${
                      materialAdvice.level === 'warn' ? 'bg-amber-50 text-amber-800 border border-amber-200'
                        : materialAdvice.level === 'ok' ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-blue-50 text-blue-800 border border-blue-200'
                    }`}>
                      {materialAdvice.level === 'warn'
                        ? <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        : <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
                      <span>{materialAdvice.msg}</span>
                    </div>
                  )}
                  <button onClick={() => setStep(3)} className="mt-4 text-sm text-gray-500 hover:text-[#004534] flex items-center gap-1">
                    <ChevronLeft className="h-4 w-4" /> Change Design
                  </button>
                </div>
              </div>

              {/* Right column: material-specific finish + common controls */}
              <div className="space-y-5">
                {/* Acrylic finish */}
                {materialFamily === 'acrylic-laminate' && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-2">Acrylic Finish</label>
                      <div className="grid grid-cols-4 gap-2">
                        {FINISHES.map((f) => (
                          <button
                            key={f.id}
                            onClick={() => setAcrylicFinish(f.id)}
                            className={`relative p-1 rounded-lg transition-all ${
                              acrylicFinish === f.id ? 'ring-2 ring-[#004534] ring-offset-2' : 'hover:ring-2 hover:ring-gray-200'
                            }`}
                            title={`${f.label} · ${f.code}`}
                            data-testid={`acrylic-finish-${f.id}`}
                          >
                            <div className={`aspect-square rounded-md ${f.border ? 'border border-gray-200' : ''}`} style={{ backgroundColor: f.color }} />
                            <span className="block text-[10px] text-center mt-1 text-gray-600 truncate">{f.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-2">Surface Texture</label>
                      <div className="grid grid-cols-2 gap-2">
                        {TEXTURES.filter((t) => t.id !== 'veneer' && t.id !== 'pu-paint').map((t) => (
                          <button
                            key={t.id}
                            onClick={() => setAcrylicTexture(t.id)}
                            className={`text-left px-3 py-2 rounded-lg border transition-all ${
                              acrylicTexture === t.id ? 'border-[#004534] bg-[#004534]/5' : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            data-testid={`acrylic-texture-${t.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-[#004534]">{t.label}</span>
                              <span className="text-[9px] px-1.5 py-0.5 bg-[#D4AF37]/25 text-[#7A6621] rounded font-semibold">{t.badge}</span>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5">{t.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Mica */}
                {materialFamily === 'mica' && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-2">CriLam Mica Shade</label>
                      <div className="grid grid-cols-5 gap-2 max-h-64 overflow-y-auto pr-1">
                        {MICA_PALETTE.map((m) => (
                          <button
                            key={m.id}
                            onClick={() => setMicaShade(m.id)}
                            className={`relative p-1 rounded-lg transition-all ${
                              micaShade === m.id ? 'ring-2 ring-[#004534] ring-offset-2' : 'hover:ring-2 hover:ring-gray-200'
                            }`}
                            title={m.name}
                            data-testid={`mica-shade-${m.id}`}
                          >
                            <div className="aspect-square rounded-md border border-gray-100" style={{ backgroundColor: m.hex }} />
                            <span className="block text-[9px] text-center mt-1 text-gray-600 truncate leading-tight">{m.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-2">
                        Prefer a specific Mica brand code?
                        <span className="text-[11px] font-normal text-gray-500 ml-1">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={micaCode}
                        onChange={(e) => setMicaCode(e.target.value)}
                        placeholder="e.g. Merino 4501 · Greenlam 3212 · Sunmica 6042"
                        className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-[#004534] focus:outline-none"
                        data-testid="mica-code-input"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        We&apos;re compatible with all major Mica brands — Merino, Greenlam, Sunmica, Century, and more.
                      </p>
                    </div>
                  </>
                )}

                {/* Veneer */}
                {materialFamily === 'veneer' && (
                  <div>
                    <label className="block text-sm font-semibold text-[#004534] mb-2">Wood Species (10 · Real Natural Veneer)</label>
                    <div className="grid grid-cols-2 gap-3">
                      {VENEER_SPECIES.map((v) => (
                        <button
                          key={v.id}
                          onClick={() => setVeneerSpecies(v.id)}
                          className={`text-left rounded-lg overflow-hidden transition-all border ${
                            veneerSpecies === v.id ? 'border-[#004534] ring-2 ring-[#004534]/40' : 'border-gray-200 hover:border-[#004534]'
                          }`}
                          data-testid={`veneer-species-${v.id}`}
                        >
                          <div className="aspect-[3/2] w-full">
                            {veneerGrainSvg(v.id)}
                          </div>
                          <div className="p-2.5 bg-white">
                            <div className="flex items-center justify-between gap-1">
                              <span className="text-sm font-bold text-[#004534] truncate">{v.name}</span>
                              {v.cncCompat === 'medium' && <span className="text-[9px] px-1 py-0.5 bg-amber-100 text-amber-800 rounded font-bold">Med CNC</span>}
                            </div>
                            <p className="text-[10px] text-gray-500 mt-0.5">{v.origin}</p>
                            <p className="text-[10px] text-gray-600 mt-1 line-clamp-2">{v.story}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* PU Paint */}
                {materialFamily === 'pu-paint' && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-2">CriLam PU Paint Palette</label>
                      <div className="grid grid-cols-6 gap-2 max-h-56 overflow-y-auto pr-1">
                        {PU_PAINT_PALETTE.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => { setPuColor(c.id); setUseCustomHex(false); }}
                            className={`relative p-1 rounded-lg transition-all ${
                              puColor === c.id && !useCustomHex ? 'ring-2 ring-[#004534] ring-offset-2' : 'hover:ring-2 hover:ring-gray-200'
                            }`}
                            title={c.name}
                            data-testid={`pu-color-${c.id}`}
                          >
                            <div className="aspect-square rounded-md border border-gray-100" style={{ backgroundColor: c.hex }} />
                            <span className="block text-[9px] text-center mt-1 text-gray-600 truncate leading-tight">{c.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={useCustomHex}
                          onChange={(e) => setUseCustomHex(e.target.checked)}
                          className="w-4 h-4 accent-[#004534]"
                          data-testid="pu-custom-toggle"
                        />
                        <label className="text-sm font-semibold text-[#004534]">Enter custom colour</label>
                      </div>
                      {useCustomHex && (
                        <div className="grid grid-cols-2 gap-2 items-center">
                          <input
                            type="color"
                            value={puHex || '#F5F3EE'}
                            onChange={(e) => setPuHex(e.target.value)}
                            className="w-full h-10 rounded cursor-pointer"
                            data-testid="pu-hex-picker"
                          />
                          <input
                            type="text"
                            value={puHex}
                            onChange={(e) => setPuHex(e.target.value)}
                            placeholder="Paste RAL / Asian Paints code"
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-[#004534] focus:outline-none"
                            data-testid="pu-code-input"
                          />
                        </div>
                      )}
                      <p className="text-[11px] text-gray-500 mt-2">
                        Any Asian Paints / RAL / Berger code accepted — we&apos;ll match it exactly at the factory.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-2">Paint Sheen</label>
                      <div className="grid grid-cols-3 gap-2">
                        {PU_SHEENS.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => setPuSheen(s.id)}
                            className={`text-left px-3 py-2 rounded-lg border transition-all ${
                              puSheen === s.id ? 'border-[#004534] bg-[#004534]/5' : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            data-testid={`pu-sheen-${s.id}`}
                          >
                            <div className="text-sm font-semibold text-[#004534]">{s.label}</div>
                            <p className="text-[10px] text-gray-500">{s.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Common: Size */}
                <div>
                  <label className="block text-sm font-semibold text-[#004534] mb-2">Door Size</label>
                  <div className="grid grid-cols-2 gap-2">
                    {DOOR_SIZES.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setDoorSize(s.id)}
                        className={`text-left px-3 py-2 rounded-lg border transition-all ${
                          doorSize === s.id ? 'border-[#004534] bg-[#004534]/5' : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        data-testid={`door-size-${s.id}`}
                      >
                        <div className="text-sm font-semibold text-[#004534]">{s.label}</div>
                        <p className="text-[11px] text-gray-500">{s.desc}</p>
                      </button>
                    ))}
                  </div>
                  {doorSize === 'custom' && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="text-xs text-gray-500">Width (ft)</label>
                        <input type="number" step="0.1" value={customW} onChange={(e) => setCustomW(e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-[#004534] focus:outline-none"
                          data-testid="door-custom-width" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Height (ft)</label>
                        <input type="number" step="0.1" value={customH} onChange={(e) => setCustomH(e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-[#004534] focus:outline-none"
                          data-testid="door-custom-height" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Single/Double */}
                <div>
                  <label className="block text-sm font-semibold text-[#004534] mb-2">Configuration</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setDoubleDoor(false)}
                      className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                        !doubleDoor ? 'bg-[#004534] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#004534]'
                      }`} data-testid="door-config-single">Single Door</button>
                    <button onClick={() => setDoubleDoor(true)}
                      className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                        doubleDoor ? 'bg-[#004534] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#004534]'
                      }`} data-testid="door-config-double">Double Door</button>
                  </div>
                </div>

                {/* Handle */}
                <div>
                  <label className="block text-sm font-semibold text-[#004534] mb-2">Handle Finish</label>
                  <div className="grid grid-cols-5 gap-2">
                    {DOOR_HANDLES.map((h) => (
                      <button key={h.id} onClick={() => setHandleStyle(h.id)}
                        className={`relative p-1 rounded-lg transition-all ${
                          handleStyle === h.id ? 'ring-2 ring-[#004534] ring-offset-2' : 'hover:ring-2 hover:ring-gray-200'
                        }`} title={h.label} data-testid={`door-handle-${h.id}`}>
                        <div className="aspect-square rounded-md border border-gray-100" style={{ backgroundColor: h.hex }} />
                        <span className="block text-[9px] text-center mt-1 text-gray-600 truncate">{h.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Frame */}
                <div>
                  <label className="block text-sm font-semibold text-[#004534] mb-2">Door Frame</label>
                  <div className="grid grid-cols-3 gap-2">
                    {DOOR_FRAMES.map((f) => (
                      <button key={f.id} onClick={() => setFrameMaterial(f.id)}
                        className={`text-left px-3 py-2 rounded-lg border transition-all ${
                          frameMaterial === f.id ? 'border-[#004534] bg-[#004534]/5' : 'border-gray-200 bg-white hover:border-gray-300'
                        }`} data-testid={`door-frame-${f.id}`}>
                        <div className="text-sm font-semibold text-[#004534]">{f.label}</div>
                        <p className="text-[10px] text-gray-500">{f.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Lighting */}
                <div>
                  <label className="block text-sm font-semibold text-[#004534] mb-2">Ambient Lighting</label>
                  <div className="grid grid-cols-3 gap-2">
                    {LIGHTINGS.map((l) => (
                      <button key={l.id} onClick={() => setLighting(l.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          lighting === l.id ? 'bg-[#004534] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#004534]'
                        }`} data-testid={`door-lighting-${l.id}`}>
                        <span className="mr-1">{l.icon}</span>{l.label}
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>
                )}

                <button
                  onClick={generateDoor}
                  disabled={isGenerating}
                  className="w-full py-4 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-[#004534] hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="generate-door-btn"
                >
                  {isGenerating ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Generating Door (30-60s)…</>
                  ) : (
                    <><Wand2 className="h-5 w-5" /> Generate My {currentMaterial.name.split(' ')[0]} Door</>
                  )}
                </button>

                {isGenerating && (
                  <div className="p-4 bg-[#004534]/5 border border-[#004534]/10 rounded-lg text-center">
                    <p className="text-sm font-medium text-[#004534] animate-pulse">{loadingMessages[loadingMsgIndex]}</p>
                    <p className="text-xs text-gray-500 mt-1">Please keep this tab open.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 5: Result */}
          {step === 5 && result && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 mb-2">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#004534] font-['Outfit']">Your CriLam Door</h2>
                  <NailFreeBadge />
                </div>
                <p className="text-gray-500">
                  {currentMaterial?.name} · {isCnc ? (result.cnc_pattern_name) : 'Plain Shutter'} · {finishDisplay} · {DOOR_ROOMS.find((r) => r.id === roomLocation)?.label}
                </p>
              </div>

              {beforeAfterMode && customCncPreview ? (
                <BeforeAfterSlider beforeSrc={customCncPreview} afterSrc={result.image_url} beforeLabel="Your Sketch" afterLabel="CriLam Rendered" />
              ) : (
                <div className="relative rounded-xl overflow-hidden shadow-xl bg-gray-100">
                  <img src={result.image_url} alt="Generated CriLam Door" className="w-full h-auto" />
                  <div className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37] text-[#004534] text-xs font-bold rounded-full">
                    CriLam AI Design Studio
                  </div>
                </div>
              )}

              <div className="mt-6 bg-white rounded-xl p-6 border border-gray-200 grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-bold text-[#004534] mb-3">Product Specifications</h4>
                  <div className="space-y-1.5 text-sm text-gray-700">
                    <div className="flex justify-between"><span className="text-gray-500">Product</span><span className="font-medium">CriLam {currentMaterial?.name}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Design</span><span className="font-medium">{isCnc ? (result.cnc_pattern_name) : 'Plain Shutter'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Finish</span><span className="font-medium">{finishDisplay}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Configuration</span><span className="font-medium">{doubleDoor ? 'Double Door' : 'Single Door'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Size</span><span className="font-medium">{doorSize === 'custom' ? `${customW}×${customH} ft` : DOOR_SIZES.find((s) => s.id === doorSize)?.label}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Handle</span><span className="font-medium">{DOOR_HANDLES.find((h) => h.id === handleStyle)?.label}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Frame</span><span className="font-medium">{DOOR_FRAMES.find((f) => f.id === frameMaterial)?.label}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Construction</span><span className="font-medium text-[#7A6621]">PUR-Bonded · Nail-Free</span></div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#004534] mb-3">Room-Suitability</h4>
                  <RoomSuitabilityBadges roomId={roomLocation} materialId={materialFamily} />
                  <h4 className="text-sm font-bold text-[#004534] mt-4 mb-2">Care Instructions</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {(result.care_instructions || MATERIAL_CARE[materialFamily] || []).map((c, i) => (
                      <li key={i} className="flex items-start gap-1.5"><CheckCircle className="h-3 w-3 text-[#004534] mt-0.5 flex-shrink-0" /> {c}</li>
                    ))}
                  </ul>
                  <div className="mt-3 flex items-start gap-2 text-xs text-gray-600 bg-[#D4AF37]/10 rounded-lg p-3">
                    <Info className="h-3.5 w-3.5 text-[#7A6621] mt-0.5 flex-shrink-0" />
                    <p>10-year written CriLam guarantee · Made at India&apos;s leading PUR-bonded CNC facility in Kanpur.</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <button onClick={shareToWhatsApp} className="flex items-center gap-2 px-5 py-3 bg-[#25D366] text-white rounded-lg hover:bg-[#128C7E] transition-colors" data-testid="door-whatsapp-btn">
                  <MessageCircle className="h-5 w-5" /> Share on WhatsApp
                </button>
                <button onClick={downloadImage} className="flex items-center gap-2 px-5 py-3 bg-[#004534] text-white rounded-lg hover:bg-[#D4AF37] hover:text-[#004534] transition-colors" data-testid="door-download-btn">
                  <Download className="h-5 w-5" /> Download Image
                </button>
                <button onClick={downloadPDF} disabled={pdfGenerating} className="flex items-center gap-2 px-5 py-3 border-2 border-[#004534] text-[#004534] rounded-lg hover:bg-[#004534] hover:text-white transition-colors disabled:opacity-60" data-testid="door-pdf-btn">
                  {pdfGenerating ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />}
                  Design Sheet (PDF)
                </button>
                <button onClick={() => setArActive(true)} className="flex items-center gap-2 px-5 py-3 border-2 border-[#D4AF37] text-[#004534] rounded-lg hover:bg-[#D4AF37] transition-colors" data-testid="door-ar-btn">
                  <Camera className="h-5 w-5" /> Try in My Doorway (AR)
                </button>
                <KenBurnsExport
                  imageUrl={result.image_url}
                  brandLabel={`${currentMaterial?.name || 'Door'} · Nail-Free`}
                  filename={`crilam-${materialFamily}-${roomLocation}-walkthrough`}
                />
                {customCncPreview && (
                  <button onClick={() => setBeforeAfterMode((m) => !m)} className="flex items-center gap-2 px-5 py-3 border-2 border-gray-300 text-gray-600 rounded-lg hover:border-[#004534] hover:text-[#004534]">
                    <Share2 className="h-5 w-5" /> {beforeAfterMode ? 'Show Full Render' : 'Compare with Sketch'}
                  </button>
                )}
              </div>

              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/contact" state={{ prefill: { subject: 'Door Quote', product: `${currentMaterial?.name} Door`, message: `Interested in a CriLam ${currentMaterial?.name} — ${isCnc ? result.cnc_pattern_name : 'Plain Shutter'} door in ${finishDisplay} for my ${DOOR_ROOMS.find((r) => r.id === roomLocation)?.label}. Nail-free PUR-bonded construction confirmed.` } }}>
                  <button className="px-6 py-3 bg-[#004534] text-white rounded-lg hover:bg-[#D4AF37] hover:text-[#004534] font-bold">
                    Get Exact Quote →
                  </button>
                </Link>
                <button onClick={reset} className="px-6 py-3 border-2 border-gray-300 text-gray-600 rounded-lg hover:border-[#004534] hover:text-[#004534]" data-testid="door-new-design-btn">
                  Design Another Door
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {arActive && result && (
          <ARDoorPreview overlayImageUrl={result.image_url} onClose={() => setArActive(false)} brandLabel={`CriLam ${currentMaterial?.name} · Try In Your Room`} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIDoorDesigner;
